import { XIcon } from '@phosphor-icons/react'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, type ReactNode } from 'react'

import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'

/**
 * Hoja que sube desde abajo.
 *
 * Para consultar un detalle sin perder de vista de dónde salió. La hoja a
 * pantalla completa (`FormSheet`) es para capturar: reemplaza el contexto
 * porque escribir exige toda la atención. Esta es para mirar, y por eso deja
 * asomar la pantalla de origen detrás del velo — al cerrarla uno sabe que
 * vuelve al mismo sitio, sin el sobresalto de un cambio de pantalla completo.
 *
 * Sube desde abajo, que es de donde viene el pulgar, y se puede cerrar de tres
 * maneras: el botón, tocar fuera y arrastrar hacia abajo. Con una sola vía de
 * salida, quien no la encuentra queda atrapado.
 */
export function HojaInferior({
  abierta,
  titulo,
  descripcion,
  onCerrar,
  children,
}: {
  abierta: boolean
  titulo: string
  descripcion?: string
  onCerrar: () => void
  children: ReactNode
}) {
  // Escape cierra, como cualquier diálogo. En escritorio es el primer reflejo.
  useEffect(() => {
    if (!abierta) return
    const alTeclear = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCerrar()
    }
    window.addEventListener('keydown', alTeclear)
    return () => window.removeEventListener('keydown', alTeclear)
  }, [abierta, onCerrar])

  // Bloquear el desplazamiento del fondo mientras la hoja está arriba: sin
  // esto, arrastrar dentro de la hoja mueve la página de atrás.
  useEffect(() => {
    if (!abierta) return
    const previo = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previo
    }
  }, [abierta])

  return (
    <AnimatePresence>
      {abierta ? (
        <>
          <motion.div
            className="fixed inset-0 z-50"
            style={{ background: 'var(--scrim)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onCerrar}
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label={titulo}
            className={cn(
              'fixed inset-x-0 bottom-0 z-50 flex max-h-[85dvh] flex-col',
              'rounded-t-sheet border-t border-line bg-bg',
            )}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 380, damping: 38 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.5 }}
            onDragEnd={(_, info) => {
              // Se cierra por distancia o por velocidad: un tirón corto pero
              // rápido es tan claro como un arrastre largo y lento.
              if (info.offset.y > 120 || info.velocity.y > 600) {
                haptic('light')
                onCerrar()
              }
            }}
          >
            {/* Asa: dice «esto se arrastra» sin una sola palabra. */}
            <div className="flex justify-center pt-2.5" aria-hidden>
              <span className="h-1 w-9 rounded-pill bg-line" />
            </div>

            <header className="px-safe flex items-start gap-3 pb-3 pt-2">
              <div className="min-w-0 flex-1">
                <h2 className="truncate font-display text-[19px] font-bold tracking-[-0.02em] text-ink">
                  {titulo}
                </h2>
                {descripcion ? (
                  <p className="mt-0.5 text-[13px] leading-snug text-ink-soft">{descripcion}</p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => {
                  haptic('light')
                  onCerrar()
                }}
                aria-label="Cerrar"
                className="flex size-11 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-ink-soft active:scale-95"
              >
                <XIcon size={17} weight="bold" />
              </button>
            </header>

            <div className="scroll-contain px-safe flex-1 overflow-y-auto pb-safe">
              <div className="mx-auto flex max-w-lg flex-col gap-2 pb-4">{children}</div>
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  )
}

/** Fila de desglose: concepto a la izquierda, monto a la derecha. */
export function FilaDetalle({
  titulo,
  detalle,
  monto,
  color,
  onClick,
}: {
  titulo: string
  detalle?: string
  monto: ReactNode
  /** Punto de color a la izquierda, para ligar la fila con su tramo. */
  color?: string
  onClick?: () => void
}) {
  const Elemento = onClick ? 'button' : 'div'
  return (
    <Elemento
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className={cn(
        'flex min-h-[52px] w-full items-center gap-3 rounded-card bg-surface px-3.5 py-2.5 text-left',
        onClick && 'active:scale-[0.99]',
      )}
    >
      {color ? (
        <span className="size-2.5 shrink-0 rounded-full" style={{ background: color }} aria-hidden />
      ) : null}
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-medium text-ink">{titulo}</span>
        {detalle ? (
          <span className="block truncate text-[12px] text-ink-soft">{detalle}</span>
        ) : null}
      </span>
      <span className="shrink-0">{monto}</span>
    </Elemento>
  )
}
